/*
 * Инструкции по установке приложения — по одной на каждый браузер.
 *
 * Разные браузеры прячут установку в разных местах, и общий текст «найдите
 * пункт установки» помогает плохо. Поэтому здесь честные шаги для каждого
 * распространённого случая, а где браузер ставить не умеет — прямо об этом
 * сказано вместо правдоподобных, но неработающих шагов.
 *
 * Рисунки ([StepIllustration](./StepIllustration.svelte)) нарисованы под
 * Safari на iPhone и остаются только у него: показывать чужой интерфейс к
 * чужим шагам хуже, чем не показывать ничего.
 */

import type { BrowserName, PlatformName } from '$utils/browser';
import type { ArtName } from './StepIllustration.svelte';

export interface InstallStep {
	text: string;
	/**
	 * Какой рисунок стоит у шага. Имя, а не номер: раньше картинки считались
	 * по порядку, и стоило переписать тексты — они молча разъехались.
	 */
	art: ArtName;
}

export interface InstallGuide {
	id: string;
	/** Как называется случай в списке «у меня другой браузер». */
	title: string;
	steps: InstallStep[];
	/** Приписка под шагами: оговорка или совет, если что-то пошло не так. */
	note?: string;
	/** Браузер ставить приложения не умеет: шагов нет, есть объяснение. */
	unsupported?: boolean;
}

const IOS_SAFARI: InstallGuide = {
	id: 'safari-ios',
	title: 'Safari на iPhone и iPad',
	steps: [
		{ text: 'Нажмите «Поделиться» — квадрат со стрелкой в нижней панели', art: 'ios-share' },
		{ text: 'Пролистайте список вниз', art: 'ios-sheet' },
		{ text: 'Нажмите «На экран „Домой“»', art: 'ios-add-row' },
		{ text: 'Подтвердите — значок появится на главном экране', art: 'ios-confirm' },
		{ text: 'Откройте ANDRESOT с этого значка и войдите', art: 'home-icon' }
	]
};

const IOS_OTHER: InstallGuide = {
	id: 'other-ios',
	title: 'Chrome, Firefox или Edge на iPhone',
	steps: [
		{ text: 'Нажмите «Поделиться» в панели браузера', art: 'ios-share-top' },
		{ text: 'Выберите «На экран „Домой“»', art: 'ios-add-row' },
		{ text: 'Подтвердите — значок появится на главном экране', art: 'ios-confirm' }
	],
	note: 'Если такого пункта нет, откройте web.andresot.ru в Safari — оттуда установка есть всегда.'
};

const ANDROID_CHROMIUM: InstallGuide = {
	id: 'chromium-android',
	title: 'Chrome, Edge, Opera или Яндекс на Android',
	steps: [
		{ text: 'Откройте меню браузера — три точки справа сверху', art: 'android-menu' },
		{
			text: 'Выберите «Установить приложение» или «Добавить на главный экран»',
			art: 'android-install-row'
		},
		{ text: 'Подтвердите — значок появится среди остальных приложений', art: 'home-icon' }
	]
};

const ANDROID_SAMSUNG: InstallGuide = {
	id: 'samsung-android',
	title: 'Samsung Internet',
	steps: [
		{ text: 'Нажмите значок с тремя полосками внизу справа', art: 'samsung-bar' },
		{ text: 'Выберите «Добавить страницу» → «На главный экран»', art: 'samsung-add-page' },
		{ text: 'Подтвердите — значок появится среди остальных приложений', art: 'home-icon' }
	]
};

const ANDROID_FIREFOX: InstallGuide = {
	id: 'firefox-android',
	title: 'Firefox на Android',
	steps: [
		{ text: 'Откройте меню браузера — три точки справа', art: 'firefox-menu' },
		{ text: 'Выберите «Установить» или «Добавить на главный экран»', art: 'android-install-row' },
		{ text: 'Подтвердите — значок появится среди остальных приложений', art: 'home-icon' }
	]
};

const DESKTOP_CHROMIUM: InstallGuide = {
	id: 'chromium-desktop',
	title: 'Chrome, Edge, Opera или Яндекс на компьютере',
	steps: [
		{ text: 'Найдите значок установки справа в адресной строке', art: 'desktop-omnibox' },
		{
			text: 'Если его нет — откройте меню браузера и выберите «Установить ANDRESOT»',
			art: 'desktop-menu'
		},
		{ text: 'Подтвердите — приложение откроется своим окном', art: 'desktop-window' }
	]
};

const DESKTOP_SAFARI: InstallGuide = {
	id: 'safari-macos',
	title: 'Safari на Mac',
	steps: [
		{ text: 'Откройте меню «Файл» в строке сверху', art: 'mac-menubar' },
		{ text: 'Выберите «Добавить в Dock»', art: 'mac-add-dock' },
		{ text: 'Подтвердите — приложение появится в Dock', art: 'mac-dock' }
	],
	note: 'Пункт есть в macOS Sonoma и новее. На старых версиях Safari устанавливать приложения не умеет.'
};

const DESKTOP_FIREFOX: InstallGuide = {
	id: 'firefox-desktop',
	title: 'Firefox на компьютере',
	unsupported: true,
	steps: [],
	note: 'Firefox на компьютере не умеет устанавливать веб-приложения — такой возможности в нём просто нет. Поставьте ANDRESOT из Chrome, Edge или Яндекс.Браузера, а Firefox можно продолжать пользоваться как обычно.'
};

const UNKNOWN: InstallGuide = {
	id: 'unknown',
	title: 'Другой браузер',
	steps: [
		{ text: 'Откройте меню браузера', art: 'android-menu' },
		{
			text: 'Найдите «Установить приложение» или «Добавить на главный экран»',
			art: 'android-install-row'
		},
		{ text: 'Подтвердите — значок появится среди остальных приложений', art: 'home-icon' }
	],
	note: 'Если такого пункта нет, браузер не умеет ставить веб-приложения. Откройте web.andresot.ru в Chrome, Edge или Safari.'
};

/** Все инструкции подряд — для списка «у меня другой браузер». */
export const GUIDES: InstallGuide[] = [
	IOS_SAFARI,
	IOS_OTHER,
	ANDROID_CHROMIUM,
	ANDROID_SAMSUNG,
	ANDROID_FIREFOX,
	DESKTOP_CHROMIUM,
	DESKTOP_SAFARI,
	DESKTOP_FIREFOX,
	UNKNOWN
];

export function guideById(id: string): InstallGuide {
	return GUIDES.find((guide) => guide.id === id) ?? UNKNOWN;
}

/** Какая инструкция подходит этому браузеру на этом устройстве. */
export function guideFor(browser: BrowserName, platform: PlatformName): InstallGuide {
	if (platform === 'ios') return browser === 'safari' ? IOS_SAFARI : IOS_OTHER;

	if (platform === 'android') {
		if (browser === 'firefox') return ANDROID_FIREFOX;
		if (browser === 'samsung') return ANDROID_SAMSUNG;
		if (browser === 'unknown' || browser === 'in-app') return UNKNOWN;
		return ANDROID_CHROMIUM;
	}

	// Дальше компьютер: Windows, macOS, Linux и всё, что не узналось.
	if (browser === 'firefox') return DESKTOP_FIREFOX;
	if (browser === 'safari') return DESKTOP_SAFARI;
	if (browser === 'unknown' || browser === 'in-app') return UNKNOWN;
	return DESKTOP_CHROMIUM;
}
